import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Layout, Typography, Tag, Tooltip } from 'antd';

import { useUpdateStatus } from '../hooks/useUpdateStatus';

const { Footer: AntFooter } = Layout;
const { Text } = Typography;

interface VersionInfo {
  version: string;
  baseVersion: string;
  buildNumber: string;
  gitCommit: string;
  buildTime: string;
  timestamp: number;
}

const majorMinor = (v: string): string => v.split('.').slice(0, 2).join('.');

const AppFooter: React.FC = () => {
  const [versionInfo, setVersionInfo] = useState<VersionInfo | null>(null);
  const [backendVersion, setBackendVersion] = useState<string | null>(null);

  useEffect(() => {
    fetch('/version.json')
      .then((res) => res.json())
      .then((data: VersionInfo) => setVersionInfo(data))
      .catch(() => setVersionInfo(null));

    fetch('/api/v1/version')
      .then((res) => res.json())
      .then((data: { version?: string }) => setBackendVersion(data?.version ?? null))
      .catch(() => setBackendVersion(null));
  }, []);

  const versionText = () => {
    if (!versionInfo) return 'unknown';
    const { baseVersion, buildNumber, gitCommit } = versionInfo;
    if (buildNumber === 'dev') return `${baseVersion}-dev`;
    return `${baseVersion}.${buildNumber}+${gitCommit}`;
  };

  // Статус обновления (#112). Для не-админа запрос вернёт 403 и data будет
  // undefined — метка просто не появится, отдельной проверки роли не нужно.
  const { data: updateStatus } = useUpdateStatus();
  const { t } = useTranslation();

  const mismatch =
    !!versionInfo &&
    !!backendVersion &&
    versionInfo.baseVersion !== majorMinor(backendVersion);

  return (
    <AntFooter style={{ textAlign: 'center', padding: '16px 24px' }}>
      <Text type="secondary" style={{ fontSize: '12px' }}>
        frontend {versionText()}
        {backendVersion && <> · backend {backendVersion}</>}
      </Text>
      {mismatch && (
        <Tag color="warning" style={{ marginLeft: 8 }}>
          version mismatch
        </Tag>
      )}
      {updateStatus?.enabled && updateStatus.update_available && updateStatus.latest_version && (
        <Tooltip title={t('updateNotice.current', { version: updateStatus.current_version })}>
          <Tag color="processing" style={{ marginLeft: 8 }} data-testid="footer-update-tag">
            {t('updateNotice.footerTag', { version: updateStatus.latest_version })}
          </Tag>
        </Tooltip>
      )}
    </AntFooter>
  );
};

export default AppFooter;
